import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Spin, Flex, Typography, Pagination } from '@app/ui';

import InsightsIcon from './icons/InsightsOutlined';
import { useAutomatedInsightsContext } from './context';
import AutomatedInsightsInfo from './Info';
import InsightCard from './InsightCard';
import ErrorBoundary from '@app/ErrorBoundary';
import CrashedComponent from '@app/components/errors/CrashedComponent';
import ReFilters from '@app/components/filters';
import { FILTER_ORIGIN } from '@app/constants/filters';

import './AutomatedInsights.scss';

const AutomatedInsights = () => {
    const { t } = useTranslation('home');
    const {
        page,
        pageNumber,
        isLoading,
        insights: paginatedInsights,
        setPage,
        setSelectedInsight,
        getIcon,
        selectedInsight,
    } = useAutomatedInsightsContext();

    const paginationRef = React.useRef(null);

    useEffect(() => {
        if (paginationRef.current) {
            paginationRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'end',
                inline: 'nearest',
            });
        }
    }, [page]);

    return (
        <>
            <div className="automated-insights">
                <div
                    className="automated-insights-filters"
                    style={{
                        top: 48,
                    }}
                >
                    <ErrorBoundary FallbackComponent={CrashedComponent}>
                        <ReFilters
                            origin={FILTER_ORIGIN.AUTO_INSIGHTS}
                            onSubmit={() => setPage(0)}
                        />
                    </ErrorBoundary>
                </div>
                <div
                    style={{
                        marginTop: 48,
                    }}
                >
                    <Flex vertical align="flex-start" justify="center">
                        <Flex justify="center" align="center" gap={8}>
                            <InsightsIcon
                                color="#009FDF"
                                width={26}
                                height={20}
                            />
                            <Typography.Text
                                style={{
                                    fontSize: '20px',
                                    fontWeight: 400,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    color: 'rgb(31, 31, 31)',
                                }}
                            >
                                {t('automatedInsights.title')}
                            </Typography.Text>
                        </Flex>

                        <Typography.Text
                            style={{
                                color: '#acacac',
                            }}
                        >
                            {t('automatedInsights.clickCardToSee')}
                        </Typography.Text>
                        <hr
                            style={{
                                width: '100%',
                                margin: '12px 0',
                                height: 1,
                                background: '#BFBFBF',
                            }}
                        />
                    </Flex>

                    {isLoading && (
                        <Flex
                            justify="center"
                            align="center"
                            style={{ minHeight: 300 }}
                        >
                            <Spin size="large" className="-m-t-3" />
                        </Flex>
                    )}

                    {!isLoading && paginatedInsights.length > 0 && (
                        <div className="automated-insights-cards-content-container">
                            {paginatedInsights.map(insight => (
                                <InsightCard
                                    key={insight.insight_id}
                                    insight={insight}
                                    isActive={
                                        selectedInsight?.insight_id ===
                                        insight.insight_id
                                    }
                                    icon={getIcon(insight) as string}
                                    onSelect={() => setSelectedInsight(insight)}
                                />
                            ))}
                        </div>
                    )}
                    {paginatedInsights.length === 0 && !isLoading && (
                        <div className="automated-insights-cards-content-no-data">
                            <div className="automated-insights-cards-content-no-data-info">
                                <div className="automated-insights-cards-content-no-data-info-icon" />
                                <p className="automated-insights-cards-content-no-data-info-text">
                                    {t('automatedInsights.noInsightsAvailable')}
                                    <a
                                        className="-m-h-1 automated-insights-cards-content-no-data-info-link"
                                        href="https://relometrics.zendesk.com/hc/en-us/articles/31882713117069-Automated-Insights-FAQ"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {t('automatedInsights.faq')}
                                    </a>
                                </p>
                            </div>
                        </div>
                    )}
                    {paginatedInsights.length > 0 && (
                        <Flex
                            justify="flex-end"
                            style={{
                                marginTop: 24,
                                marginBottom: 24,
                                width: '100%',
                            }}
                        >
                            <Pagination
                                defaultCurrent={1}
                                total={pageNumber * 6}
                                pageSize={6}
                                onChange={setPage}
                                showSizeChanger={false}
                            />
                        </Flex>
                    )}
                </div>
                <AutomatedInsightsInfo />
            </div>
            <div className="automated-insights-footer" />
        </>
    );
};

export default AutomatedInsights;
